import React, { useState, useEffect, useMemo } from 'react';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Navbar } from './components/Navbar';
import { Home } from './components/Home';
import { Booking } from './components/Booking';
import { ServicesPage } from './components/ServicesPage';
import { AboutPage } from './components/AboutPage';
import { GalleryPage } from './components/GalleryPage';
import { Footer } from './components/Footer';
import { ViewState, SERVICES } from './types';
import { Seo, pathForView, viewFromPath } from './seo';
import BlogPage from './components/BlogPage';
import BlogPostPage from './components/BlogPostPage';

gsap.registerPlugin(ScrollTrigger);

export default function App() {
  const [currentView, setCurrentView] = useState<ViewState>(() => viewFromPath(window.location.pathname));
  const [preSelectedService, setPreSelectedService] = useState<string | undefined>();
  const [blogParams, setBlogParams] = useState<{ slug?: string } | undefined>();

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);
    
    const scrollAnimations = () => {
      gsap.utils.toArray('.fade-in-up').forEach((elem: any) => {
        gsap.fromTo(elem, 
          { y: 50, opacity: 0 },
          { 
            y: 0, 
            opacity: 1, 
            duration: 1, 
            ease: 'power3.out',
            scrollTrigger: {
              trigger: elem,
              start: 'top 85%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      });
    };

    const timeout = setTimeout(scrollAnimations, 100);

    return () => {
      lenis.destroy();
      clearTimeout(timeout);
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, [currentView]);

  useEffect(() => {
    const onPopState = () => {
      setCurrentView(viewFromPath(window.location.pathname));
      window.scrollTo(0, 0);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const handleBook = (serviceId?: string) => {
    let message = `Hi MIER! I'd like to book an appointment. I'm visiting from your website (${window.location.origin}).`;
    
    if (serviceId) {
      const service = SERVICES.find((s: any) => s.id === serviceId);
      if (service) {
        message = `Hi MIER! I'd like to book an appointment for the ${service.name} service. I'm visiting from your website (${window.location.origin}).`;
      }
    }
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://api.whatsapp.com/send/?phone=971543446466&text=${encodedMessage}`, '_blank');
  };

  const navTo = (view: ViewState, params?: any) => {
    const nextPath = pathForView(view);
    if (window.location.pathname !== nextPath) {
      window.history.pushState({}, '', nextPath);
    }
    setCurrentView(view);
    if (params) setBlogParams(params);
    window.scrollTo(0, 0);
  };

  const currentServiceFilter = useMemo(() => {
    if (currentView === 'hair-salon') return ['Hair Services', 'Haircut', 'Hair Coloring', 'Hair Treatment'];
    if (currentView === 'nail-salon') return ['Nails'];
    if (currentView === 'skin-treatments') return ['Facials & Skincare', 'Aesthetics'];
    if (currentView === 'lash-brow') return ['Lashes', 'Brows'];
    if (currentView === 'bridal') return ['Bridal'];
    return undefined;
  }, [currentView]);

  return (
    <div className="font-sans antialiased text-mier bg-stone-50 min-h-screen">
      <Seo view={currentView} blogSlug={blogParams?.slug} />
      <Navbar currentView={currentView} setView={navTo} />
      
      <main id="main-content">
        {(currentView === 'home' || currentView.startsWith('location-')) && (
          <Home 
            view={currentView}
            onBook={handleBook} 
            onViewServices={() => navTo('services')} 
            onViewAbout={() => navTo('about')}
            onViewGallery={() => navTo('gallery')}
            onNavigate={navTo}
          />
        )}
        
        {(currentView === 'services' || currentServiceFilter) && (
          <ServicesPage onBook={handleBook} filterCategories={currentServiceFilter} view={currentView} />
        )}
        
        {currentView === 'about' && (
          <AboutPage />
        )}
        
        {currentView === 'gallery' && (
          <GalleryPage onBook={handleBook} />
        )}

        {currentView === 'blog' && (
          <BlogPage onNavigate={navTo} />
        )}

        {currentView === 'blog-post' && (
          <BlogPostPage params={blogParams} onNavigate={navTo} />
        )}

        {currentView === 'booking' && (
          <Booking 
            onBack={() => {
              navTo('home');
              setPreSelectedService(undefined);
            }} 
            preSelectedService={preSelectedService} 
          />
        )}
      </main>

      {currentView !== 'booking' && <Footer onBook={handleBook} onNav={navTo} />}

      {/* Floating CTA - WhatsApp Optimization */}
      {currentView !== 'booking' && (
        <>
          <div className="fixed bottom-6 left-6 z-50">
            <button 
              onClick={() => handleBook()}
              className="bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform duration-300 flex items-center justify-center group"
              title="Chat on WhatsApp"
            >
              <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-2 transition-all duration-300 text-xs uppercase tracking-widest font-medium whitespace-nowrap">Chat with us</span>
            </button>
          </div>

          <div className="fixed bottom-6 right-6 z-50 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)]">
            <button 
              onClick={() => navTo('booking')}
              className="bg-mier text-stone-50 px-8 py-5 rounded-full hover:-translate-y-1 hover:bg-mier-dark transition-all duration-300 text-xs tracking-[0.2em] uppercase font-medium shadow-2xl"
            >
              Book Now
            </button>
          </div>
        </>
      )}
    </div>
  );
}

